import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Token ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      if (!window.location.pathname.includes('/login')) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userInfo');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// API service methods
export const apiService = {
  // Users
  getUsers: (page = 1) => api.get(`/users/?page=${page}`),
  getUser: (id) => api.get(`/users/${id}/`),
  createUser: (data) => api.post('/users/', data),
  updateUser: (id, data) => api.put(`/users/${id}/`, data),
  deleteUser: (id) => api.delete(`/users/${id}/`),
  deactivateUser: (id) => api.post(`/users/${id}/deactivate/`),
  activateUser: (id) => api.post(`/users/${id}/activate/`),
  
  // Projects
  getProjects: (page = 1) => api.get(`/projects/admin/all/?page=${page}`),
  getUserProjects: (userId) => api.get(`/projects/admin/user/${userId}/`),
  getAllProjectsForAdmin: (page = 1) => api.get(`/projects/admin/all/?page=${page}`),
  createProject: (data) => api.post('/projects/', data),
  updateProject: (id, data) => api.put(`/projects/${id}/`, data),
  assignConsultant: (projectId, consultantId) => api.post(`/projects/admin/${projectId}/assign-consultant/`, { consultant_id: consultantId }),
  updateProjectStatus: (projectId, status) => api.post(`/projects/admin/${projectId}/update-status/`, { status }),
  updateDocumentStatus: (projectId, documentType, status) => api.post(`/projects/admin/${projectId}/update-document-status/`, { document_type: documentType, status }),
  
  // User Profile and Documents
  getUserProfile: () => api.get('/users/profile/documents/'),
  uploadDocuments: (formData) => api.post('/users/profile/upload-documents/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateUserDocumentStatus: (userId, documentType, status) => api.post(`/users/${userId}/update-document-status/`, { document_type: documentType, status }),
  

  
  // Schemes
  getSchemes: (page = 1) => api.get(`/schemes/?page=${page}`),
  getAdminSchemes: () => api.get('/schemes/admin/all/'),
  createScheme: (data) => api.post('/schemes/', data),
  updateScheme: (id, data) => api.put(`/schemes/${id}/`, data),
  deleteScheme: (id) => api.delete(`/schemes/${id}/`),
  toggleSchemeStatus: (id) => api.post(`/schemes/${id}/toggle-status/`),
  
  // Staff/Consultants
  getStaff: (page = 1) => api.get(`/users/staff/?page=${page}`),
  createStaff: (data) => api.post('/users/', { ...data, is_staff: true }),
  updateStaff: (id, data) => api.put(`/users/${id}/`, data),
  deleteStaff: (id) => api.delete(`/users/${id}/`),
  assignProjectsToStaff: (staffId, projectIds) => api.post(`/users/staff/${staffId}/assign-projects/`, { project_ids: projectIds }),
  
  // Services
  getServices: () => api.get('/services/'),
  getActiveServices: () => api.get('/services/active/'),
  getService: (id) => api.get(`/services/${id}/`),
  createService: (data) => api.post('/services/', data),
  updateService: (id, data) => api.put(`/services/${id}/`, data),
  deleteService: (id) => api.delete(`/services/${id}/`),
  
  // Messages/Notifications
  getUnreadMessageCount: () => api.get('/projects/messages/unread-count/'),
};

export default api;